import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_vendor_profile,
  update_vendor_profile,
  upload_vendor_image,
  type UpdateVendorProfileInput,
  type VendorImageKind,
  type VendorProfile,
  set_service_area,
  fetch_payout,
  replace_payout,
  create_payout,
} from './api';

export const VENDOR_PROFILE_KEY = ['vendor', 'profile'] as const;

export function use_vendor_profile() {
  return useQuery({
    queryKey: VENDOR_PROFILE_KEY,
    queryFn: fetch_vendor_profile,
    staleTime: 0,
    retry: 0,
  });
}

export function use_update_vendor_profile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateVendorProfileInput) => update_vendor_profile(input),
    onSuccess: (data: VendorProfile) => qc.setQueryData(VENDOR_PROFILE_KEY, data),
  });
}

export function use_upload_vendor_image() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file }: { kind: VendorImageKind; file: File }) =>
      upload_vendor_image(kind, file),
    onSuccess: (data: VendorProfile) => {
      qc.setQueryData(VENDOR_PROFILE_KEY, data);
      // Every slot serves from a fixed path, so a replacement reuses the URL its
      // predecessor had. Without dropping the cached blob the vendor uploads a new
      // picture and keeps looking at the old one.
      for (const url of [data.logo_url, data.cover_url, data.profile_photo?.file_path]) {
        if (url) qc.invalidateQueries({ queryKey: ['file-blob', url] });
      }
    },
  });
}

/** Changing this moves who can find the vendor, so the profile is refetched. */
export function use_set_service_area() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: set_service_area,
    onSuccess: () => void qc.invalidateQueries({ queryKey: VENDOR_PROFILE_KEY }),
  });
}

const PAYOUT_KEY = ['vendor-profile', 'payout'] as const;

export function use_payout() {
  return useQuery({ queryKey: PAYOUT_KEY, queryFn: fetch_payout, staleTime: 0, retry: 0 });
}

/**
 * Replacing payout details starts a 30-day lockout, so the cached view must be refreshed on
 * success — otherwise the form stays open and the vendor's next attempt hits a 409 they had no
 * warning about.
 */
/** First-time setup. No cooldown is consumed, so the vendor's first real change is still free. */
export function use_create_payout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: create_payout,
    onSuccess: (view) => {
      qc.setQueryData(PAYOUT_KEY, view);
    },
  });
}

export function use_replace_payout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: replace_payout,
    onSuccess: (view) => {
      qc.setQueryData(PAYOUT_KEY, view);
    },
  });
}
